import { UsersThreeIcon } from '@phosphor-icons/react'
import { type PlainDate, formatMoney, percentOf } from '@paybae/shared'
import { useMemo, useState } from 'react'

import { Card, EmptyState, SectionHeader } from '@/components/Card'
import { DetalleDinero } from '@/components/DetalleDinero'
import { ProgressRing } from '@/components/Donut'
import { Rotulo, TarjetaTinte, type Tinte } from '@/components/Editorial'
import { ErrorState, Skeleton } from '@/components/Estado'
import {
  MapaDelDinero,
  type SeleccionMapa,
  type TenenciaDinero,
} from '@/components/MapaDelDinero'
import { PersonAvatar } from '@/components/PersonAvatar'
import { ScreenHeader } from '@/components/ScreenHeader'
import type { PlanVsRealFigures, PlanVsRealRow } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { useCashflow, useDashboard, useInformes } from '@/lib/queries'

/**
 * El mes en curso: las tres cifras y el mapa del dinero.
 *
 * Las dos piezas vivían en el inicio y de ahí se mudaron. La razón no fue
 * recortar: el inicio contesta **una** pregunta —«¿cuánto puedo gastar hoy?»— y
 * tenía trece bloques con tres donas que decían cosas parecidas en colores
 * distintos. Estas dos contestan otra cosa, «¿cómo va el mes y quién tiene el
 * dinero?», que es análisis. Aquí no estorban; allí tapaban la cifra principal.
 *
 * El mapa, además, era inútil en un hogar de una persona: una dona de un solo
 * tramo al 100%. Por eso aquí solo se dibuja cuando hay más de una bolsa que
 * comparar, y en su lugar no queda un hueco sino nada.
 */
function MesEnCurso() {
  const { data } = useDashboard()
  const { data: flujo } = useCashflow(6)
  const [detalle, setDetalle] = useState<SeleccionMapa | null>(null)

  const tenencias: TenenciaDinero[] = useMemo(() => {
    if (!data) return []
    const personas = data.members.map((m) => {
      const bolsa = data.availability.perMember[m.memberId]
      return {
        holderId: m.memberId,
        nombre: (m.nickname || m.displayName).split(' ')[0]!,
        colorToken: m.personColorToken,
        libreCents: Math.max(0, bolsa?.freeCents ?? 0),
        ahorradoCents: m.savedCents,
        gastadoCents: m.spentCents,
        deudaCents: bolsa?.cardDebtCents ?? 0,
      }
    })
    if (!data.availability.joint || !data.joint) return personas
    return [
      ...personas,
      {
        holderId: null,
        nombre: 'Fondo común',
        colorToken: null,
        libreCents: Math.max(0, data.availability.joint.freeCents),
        ahorradoCents: data.joint.savedCents,
        gastadoCents: data.joint.spentCents,
        deudaCents: data.availability.joint.cardDebtCents,
      },
    ]
  }, [data])

  if (!data) {
    return <Skeleton className="h-28 w-full rounded-hero" />
  }

  // Se suma por integrante y no se toma de una bolsa concreta: este bloque habla
  // del hogar entero, no del alcance que estuviera elegido en el inicio.
  const esperado = Object.values(data.availability.perMember).reduce(
    (suma, p) => suma + p.expectedIncomeCents,
    0,
  )
  const comprometido = data.availability.combined.committedCents
  const gastado = flujo?.currentMonth.totalExpenseCents ?? 0

  return (
    <section className="flex flex-col gap-3">
      {/* Tres tarjetas tintadas, no tres columnas con filete.
          El tinte dice de qué habla cada cifra antes de leer el rótulo:
          mantequilla lo que entra, durazno lo que sale, lavanda lo que ya tiene
          dueño. Con filetes verticales las tres se leían como una sola tabla y
          había que recorrer los rótulos para distinguirlas. */}
      <div className="grid grid-cols-3 gap-2">
        <CifraTinte tinte="butter" rotulo="Por caer" cents={esperado} nota="nómina del periodo" />
        <CifraTinte tinte="peach" rotulo="Gastado" cents={gastado} nota="este mes" />
        <CifraTinte
          tinte="lavender"
          rotulo="Comprometido"
          cents={comprometido}
          nota="fijos, tarjetas y ahorro"
        />
      </div>

      {/* El mapa solo cuando hay algo que comparar.
          Se cuentan las bolsas CON dinero, no las que existen: un hogar de
          pareja siempre trae el fondo común, y con el fondo en cero el mapa
          salía igual —una dona de una sola persona al 100%—, que es justo lo
          que esta condición quería evitar. */}
      {tenencias.filter((t) => t.libreCents + t.ahorradoCents + t.gastadoCents > 0).length > 1 ? (
        <TarjetaTinte>
          <Rotulo titulo="Quién tiene el dinero" className="mb-1" />
          <MapaDelDinero
            tenencias={tenencias}
            formatear={(c) => formatMoney(c, { hideZeroCents: true })}
            onSeleccionar={setDetalle}
          />
        </TarjetaTinte>
      ) : null}

      <DetalleDinero
        seleccion={detalle}
        nombreTitular={
          tenencias.find((t) => t.holderId === detalle?.holderId)?.nombre ?? 'Fondo común'
        }
        bolsa={
          detalle === null
            ? null
            : detalle.holderId === null
              ? data.availability.joint
              : (data.availability.perMember[detalle.holderId] ?? null)
        }
        periodoDesde={data.periodStart}
        periodoHasta={data.periodEnd}
        onCerrar={() => setDetalle(null)}
      />
    </section>
  )
}

/** Una de las tres cifras del mes, con su tinte semántico. */
function CifraTinte({
  tinte,
  rotulo,
  cents,
  nota,
}: {
  tinte: Tinte
  rotulo: string
  cents: number
  nota: string
}) {
  return (
    <TarjetaTinte tinte={tinte} className="flex flex-col gap-0.5 p-3">
      <span className="text-[9.5px] font-bold uppercase leading-tight tracking-[0.08em] text-ink-soft">
        {rotulo}
      </span>
      <span data-money className="font-display text-[17px] font-extrabold tracking-[-0.03em] text-ink">
        {formatMoney(cents, { hideZeroCents: true })}
      </span>
      {/* `soft` y no `faint`: sobre el tinte, el gris tenue no llegaba a 2.5:1
          y la nota es la que dice qué abarca la cifra. */}
      <span className="text-[10.5px] leading-tight text-ink-soft">{nota}</span>
    </TarjetaTinte>
  )
}

/**
 * Plan contra realidad (§27).
 *
 * Cada concepto es un anillo de lo real sobre lo planeado. Anillos y no barras:
 * la pregunta es «¿qué tanto de lo que dije que pasaría pasó?», y eso es una
 * proporción sobre un todo cerrado, no una longitud que comparar contra un eje.
 *
 * EL COLOR NO SIGNIFICA LO MISMO EN LOS CUATRO
 *
 * Cumplir el ahorro es bueno; gastar lo previsto y pasarse, no. Si el 120%
 * se pintara igual en los dos, el informe estaría diciendo dos cosas contrarias
 * con el mismo tono y habría que leer la etiqueta para saber cuál. Por eso:
 *
 *   menta   el resultado favorece — se ingresó, ahorró o aportó lo pactado,
 *           o se gastó dentro de lo estimado
 *   índigo  falta para llegar; no es una falla, es un avance a medias
 *   coral   **solo** cuando el gasto real pasó del estimado
 *
 * Quedarse corto de ahorro nunca se pinta de coral: es la diferencia entre un
 * informe que acompaña y uno que regaña.
 */
export function Informes() {
  const { data, isPending, isError, error, refetch } = useInformes(6)

  /** `null` = el mes en curso, que es el último del informe. */
  const [mesElegido, setMesElegido] = useState<string | null>(null)

  if (isPending) return <Cargando />

  if (isError || !data || data.meses.length === 0) {
    return (
      <>
        <ScreenHeader title="Informes" tinte="lavender" />
        <ErrorState
          titulo="No pudimos armar tus informes"
          mensaje={error instanceof Error ? error.message : 'Inténtalo de nuevo.'}
          onReintentar={() => void refetch()}
        />
      </>
    )
  }

  const mes =
    data.meses.find((m) => m.month === mesElegido) ?? data.meses[data.meses.length - 1]!

  const conDatos = mes.integrantes.filter(tieneDatos)
  // El total solo aporta algo cuando hay más de una bolsa que sumar: en un hogar
  // de una persona repetiría la tarjeta de abajo palabra por palabra.
  const mostrarTotal = conDatos.length > 1

  return (
    <>
      <ScreenHeader
        title="Informes"
        subtitle="Cómo va el mes y qué tanto se cumple lo planeado"
        tinte="lavender"
      />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        {/* Lo que estaba en el inicio y pasó a vivir aquí.
            El tablero llegó a tener trece bloques y tres donas distintas, así
            que dejó de contestar «¿cuánto puedo gastar hoy?». Estas dos piezas
            no se borraron: contestan «¿cómo va el mes?» y «¿quién tiene el
            dinero?», que son preguntas de análisis y este es el sitio donde se
            analiza. Van arriba de plan-contra-real porque hablan del presente y
            lo de abajo del pasado. */}
        <MesEnCurso />

        <SectionHeader title="Lo planeado, mes a mes" className="mb-0 mt-2" />

        {/* El mes en curso primero: es el que se consulta nueve de cada diez
            veces, y dejarlo al final obligaría a desplazar la fila para llegar
            al mes que ya estaba seleccionado. */}
        <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
          {[...data.meses].reverse().map((m) => (
            <button
              key={m.month}
              type="button"
              onClick={() => {
                haptic('selection')
                setMesElegido(m.month)
              }}
              aria-pressed={m.month === mes.month}
              className={cn(
                'flex min-h-[44px] shrink-0 items-center rounded-pill border px-3.5 text-[13px] font-medium',
                'transition-colors duration-150 active:scale-95',
                m.month === mes.month
                  ? 'border-accion bg-accion text-on-accion'
                  : 'border-line bg-surface text-ink-soft',
              )}
            >
              {etiquetaMes(m.month, data.today)}
            </button>
          ))}
        </div>

        {conDatos.length === 0 ? (
          <Card>
            <EmptyState
              title={`Sin nada registrado en ${tituloMes(mes.month).toLowerCase()}`}
              description="Cuando confirmes una nómina, registres gastos o aportes a un fondo, aquí verás cuánto de lo planeado se cumplió."
            />
          </Card>
        ) : (
          <>
            {mostrarTotal ? (
              <section>
                <SectionHeader title={tituloMes(mes.month)} />
                <BloqueConceptos
                  titulo="Todo el hogar"
                  avatar={
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-soft">
                      <UsersThreeIcon size={17} weight="fill" />
                    </span>
                  }
                  figuras={mes.total}
                />
              </section>
            ) : null}

            <section className="flex flex-col gap-3 pb-2">
              {!mostrarTotal ? <SectionHeader title={tituloMes(mes.month)} className="mb-0" /> : null}
              {conDatos.map((row) => (
                <BloqueConceptos
                  key={row.memberId ?? 'comun'}
                  titulo={row.displayName}
                  avatar={
                    row.memberId === null ? (
                      // El fondo común no es de nadie, así que no lleva color de
                      // identidad: tomarlo prestado de alguien lo haría suyo.
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-soft">
                        <UsersThreeIcon size={17} weight="fill" />
                      </span>
                    ) : (
                      <PersonAvatar name={row.displayName} token={row.personColorToken} />
                    )
                  }
                  figuras={row}
                />
              ))}
            </section>

            <p className="px-1 pb-2 text-[12px] leading-snug text-ink-faint">
              El anillo va en menta cuando el resultado te favorece y en coral solo si el gasto real
              pasó del estimado. Quedarte corto de ahorro no es una falla: va en azul, como un
              avance a medias.
            </p>
          </>
        )}
      </div>
    </>
  )
}

function BloqueConceptos({
  titulo,
  avatar,
  figuras,
}: {
  titulo: string
  avatar: React.ReactNode
  figuras: PlanVsRealFigures
}) {
  const lista = conceptos(figuras)

  return (
    <Card className="flex flex-col gap-4">
      <header className="flex items-center gap-2.5">
        {avatar}
        <p className="min-w-0 flex-1 truncate text-[15px] font-semibold text-ink">{titulo}</p>
      </header>

      {/* Dos columnas y no cuatro: a 375 px, cuatro anillos en fila dejarían el
          anillo en 60 px y la cifra de abajo partida en tres renglones. */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        {lista.map((concepto) => (
          <Celda key={concepto.key} concepto={concepto} />
        ))}
      </div>
    </Card>
  )
}

function Celda({ concepto }: { concepto: Concepto }) {
  const { esperado, real, penalizaExceso } = concepto

  // Sin plan no hay proporción que medir: el anillo se queda en su pista y la
  // cifra se enseña sola. Inventar un 100% donde no se planeó nada convertiría
  // en logro algo que nadie prometió.
  const sinPlan = esperado <= 0
  const favorece = sinPlan ? false : penalizaExceso ? real <= esperado : real >= esperado
  const color = sinPlan
    ? 'var(--chart-4)'
    : favorece
      ? 'var(--income)'
      : penalizaExceso
        ? 'var(--expense)'
        : 'var(--primary)'

  // El porcentaje del texto no se acota: pasarse del gasto es exactamente el
  // caso que hay que poder leer, y un «100%» ocultaría el 138% que fue.
  const razon = sinPlan ? null : Math.round((real / esperado) * 100)

  return (
    <div className="flex flex-col items-center gap-1.5 text-center">
      <ProgressRing percent={percentOf(real, esperado)} size={68} stroke={7} color={color}>
        <span data-money className="text-[13px] font-bold text-ink">
          {razon === null ? '—' : `${razon}%`}
        </span>
      </ProgressRing>

      <span className="text-[12px] font-semibold leading-tight text-ink">{concepto.etiqueta}</span>
      <span data-money className="text-[11px] leading-tight text-ink-soft">
        {sinPlan ? (
          <>
            {formatMoney(real, { hideZeroCents: true })} · sin plan
          </>
        ) : (
          <>
            {formatMoney(real, { hideZeroCents: true })}
            <span className="text-ink-faint"> de {formatMoney(esperado, { hideZeroCents: true })}</span>
          </>
        )}
      </span>
    </div>
  )
}

interface Concepto {
  key: string
  etiqueta: string
  /** Lo que se planeó. */
  esperado: number
  /** Lo que pasó. */
  real: number
  /** Solo el gasto: pasarse de lo estimado es lo único que se pinta en coral. */
  penalizaExceso: boolean
}

/**
 * Los cuatro conceptos de la espec, ya filtrados.
 *
 * Un concepto sin plan y sin realidad no se dibuja: cuatro anillos vacíos con
 * «$0 de $0» ocupan media pantalla para no decir nada, y esconden los que sí
 * traen información.
 */
function conceptos(f: PlanVsRealFigures): Concepto[] {
  return [
    {
      key: 'ingreso',
      etiqueta: 'Ingreso',
      esperado: f.ingresoEsperado,
      real: f.ingresoReal,
      penalizaExceso: false,
    },
    {
      key: 'ahorro',
      etiqueta: 'Ahorro',
      esperado: f.ahorroEsperado,
      real: f.ahorroReal,
      penalizaExceso: false,
    },
    {
      key: 'gasto',
      etiqueta: 'Gasto',
      esperado: f.gastoEstimado,
      real: f.gastoReal,
      penalizaExceso: true,
    },
    {
      key: 'aportacion',
      etiqueta: 'Al fondo común',
      esperado: f.aportacionEsperada,
      real: f.aportacionReal,
      penalizaExceso: false,
    },
  ].filter((c) => c.esperado > 0 || c.real > 0)
}

/** Una bolsa vacía en los cuatro conceptos no merece tarjeta propia. */
function tieneDatos(row: PlanVsRealRow): boolean {
  return conceptos(row).length > 0
}

const MESES_CORTOS = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
]
const MESES_LARGOS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

/** El año solo se dice cuando no es el actual: en la pastilla no sobra espacio. */
function etiquetaMes(month: string, hoy: PlainDate): string {
  const [year, mes] = month.split('-')
  const nombre = MESES_CORTOS[Number(mes) - 1]
  return year === hoy.slice(0, 4) ? nombre! : `${nombre} ${year!.slice(2)}`
}

function tituloMes(month: string): string {
  const [year, mes] = month.split('-')
  return `${MESES_LARGOS[Number(mes) - 1]} ${year}`
}

/**
 * Esqueleto de carga.
 *
 * Reserva la fila de pastillas y dos tarjetas con su rejilla de cuatro anillos:
 * el informe llega de una sola petición, así que todo aparece a la vez y el
 * hueco tiene que medir lo que va a ocupar.
 */
function Cargando() {
  return (
    <>
      <ScreenHeader title="Informes" subtitle="Sacando cuentas…" tinte="lavender" />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-10 w-16 shrink-0 rounded-pill" />
          ))}
        </div>

        <div className="mb-0 flex h-[22px] items-center">
          <Skeleton className="h-4 w-32" />
        </div>

        {[0, 1].map((i) => (
          <Card key={i} className="flex flex-col gap-4">
            <header className="flex items-center gap-2.5">
              <Skeleton className="size-9 shrink-0 rounded-full" />
              <Skeleton className="h-[15px] w-32" />
            </header>
            <div className="grid grid-cols-2 gap-x-3 gap-y-4">
              {[0, 1, 2, 3].map((j) => (
                <div key={j} className="flex flex-col items-center gap-1.5">
                  <Skeleton className="size-[68px] rounded-full" />
                  <Skeleton className="h-[12px] w-16" />
                  <Skeleton className="h-[11px] w-24" />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </>
  )
}
